import {
	useClientSideDataGridModel,
	UseClientSideDataGridModelParams,
	UseClientSideDataGridModelReturn,
} from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import React from "react";
import { AsyncRender } from "../../async/AsyncRender";
import { useAsyncState } from "src/utils/async/asyncState.ts";
import { removeKeyFromObject } from "src/utils/objectUtils.ts";
import { DataGridRowUpdateHandler } from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";

export interface ClientSideDataGridModelProps<TRemoteData, TRow extends object, TParams extends object>
	extends Omit<UseClientSideDataGridModelParams<TRow, TParams>, "fetchData"> {
	fetchData: (params: TParams) => Promise<TRemoteData>;
	getRows: (remoteData: TRemoteData, params?: TParams) => TRow[];
	render: (props: ClientSideDataGridModelRenderProps<TRemoteData, TRow, TParams>) => React.ReactNode;
}

export interface ClientSideDataGridModelRenderProps<TRemoteData, TRow extends object, TParams>
	extends Omit<UseClientSideDataGridModelReturn<TRow, TParams>, "dataGridProps"> {
	dataGridProps: ClientSideDataGridModelDataGridProps<TRow>;
	data: TRemoteData;
}

export interface ClientSideDataGridModelDataGridProps<TRow extends object>
	extends Required<
		Pick<
			AavoDataGridProps<TRow>,
			| "rows"
			| "refreshData"
			| "initialState"
			| "onStateChange"
			| "selectedRows"
			| "onRowSelectionChanged"
			| "getRowId"
			| "apiRef"
			| "gridId"
		>
	>, Pick<AavoDataGridProps<TRow>, "customFunctionUiActionView" | "customFunctionUiActionObjectId"> {
	updateRows: DataGridRowUpdateHandler;
}

export const ClientSideDataGridModel = <TRemoteData, TRow extends object, TParams extends object>({
	render,
	fetchData,
	getRows,
	...other
}: ClientSideDataGridModelProps<TRemoteData, TRow, TParams>) => {
	const [remoteDataAsync, setRemoteDataAsync] = useAsyncState<TRemoteData>({});
	const { dataGridProps, ...otherHookReturn } = useClientSideDataGridModel({
		...other,
		fetchData: async (params) => {
			const remoteData = await setRemoteDataAsync(() => fetchData(params));
			return getRows(remoteData, params);
		},
	});

	return (
		<AsyncRender
			asyncData={remoteDataAsync}
			reloadOnError={dataGridProps.refreshData}
			errorSx={{
				margin: 1
			}}
			content={(data) => {
				return render({
					dataGridProps: {
						...removeKeyFromObject(dataGridProps, "rowsAsync"),
						rows: dataGridProps.rowsAsync.data ?? [],
					},
					data: data,
					...otherHookReturn,
				});
			}}
		/>
	);
};
